import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const primaryNavItems = [
    { name: 'AI Core Blueprint', path: '/ai-core-blueprint' },
    { name: 'Open Source Advantage', path: '/open-source-advantage' },
    { name: 'Services', path: '/services-implementation' },
    { name: 'Case Studies', path: '/case-studies-proof' },
    { name: 'Resources', path: '/resources-hub' }
  ];

  const secondaryNavItems = [
    { name: 'About', path: '/about-ai-clean-core' },
    { name: 'Get Started', path: '/get-started' }
  ];

  const isActivePath = (path) => {
    return location.pathname === path || (path === '/homepage' && location.pathname === '/');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-executive border-b border-gray-200' 
          : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/homepage" 
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
          >
            <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center">
              <svg 
                viewBox="0 0 24 24" 
                className="w-6 h-6 lg:w-7 lg:h-7 text-white"
                fill="currentColor"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div className="hidden sm:block">
              <div className="text-xl lg:text-2xl font-bold text-brand-primary">
                AI Clean Core
              </div>
              <div className="text-xs lg:text-sm text-text-secondary -mt-1">
                Enterprise AI Independence
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {primaryNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`text-sm font-medium transition-colors duration-200 hover:text-brand-primary ${
                  isActivePath(item?.path)
                    ? 'text-brand-primary border-b-2 border-brand-primary pb-1' :'text-text-primary'
                }`}
              >
                {item?.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA and More Menu */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="relative group">
              <Button
                variant="ghost"
                className="text-text-secondary hover:text-text-primary"
                iconName="MoreHorizontal"
                iconPosition="right"
              >
                More
              </Button>
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-executive-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
                <div className="py-2">
                  {secondaryNavItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`block px-4 py-2 text-sm transition-colors duration-200 hover:bg-gray-50 ${
                        isActivePath(item?.path)
                          ? 'text-brand-primary bg-gray-50' :'text-text-primary'
                      }`}
                    >
                      {item?.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Button
              variant="default"
              className="bg-brand-primary hover:bg-brand-primary/90"
              iconName="ArrowRight"
              iconPosition="right"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={toggleMenu}
            iconName={isMenuOpen ? "X" : "Menu"}
          />
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden transition-all duration-300 ease-in-out ${
            isMenuOpen 
              ? 'max-h-96 opacity-100' :'max-h-0 opacity-0 overflow-hidden'
          }`}
        >
          <nav className="px-4 py-4 bg-white border-t border-gray-200">
            <div className="space-y-3">
              {primaryNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-2 text-base font-medium transition-colors duration-200 ${
                    isActivePath(item?.path)
                      ? 'text-brand-primary' :'text-text-primary hover:text-brand-primary'
                  }`}
                >
                  {item?.name}
                </Link>
              ))}
              
              <div className="border-t border-gray-200 pt-3 mt-3">
                {secondaryNavItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block py-2 text-base transition-colors duration-200 ${
                      isActivePath(item?.path)
                        ? 'text-brand-primary' :'text-text-secondary hover:text-brand-primary'
                    }`}
                  >
                    {item?.name}
                  </Link>
                ))}
              </div>

              <div className="pt-4">
                <Button
                  variant="default"
                  fullWidth
                  className="bg-brand-primary hover:bg-brand-primary/90"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </Button>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;